import { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, InputAdornment, TextField, Typography, alpha, useTheme,
} from '@mui/material';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import axios from 'axios';
import { Api } from '../../../shared/services/api/axiosApi';
import { IStatusCertificado, atualizarStatusCertificado, useStatusCertificado } from '../../../shared/components/certificado/AvisoCertificado';

// Cartão "Certificado digital" da tela de Configurações da empresa: mostra a
// situação do certificado (titular, válido até, dias restantes) e envia um
// novo — o backend confere senha, validade e CNPJ antes de gravar e salva
// com o nome padrão no lugar do antigo (ver EnviarCertificado.ts). Também
// serve pra só corrigir a senha do certificado atual.

const dataBR = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '');

const situacaoVisual = (s: IStatusCertificado | null) => {
  if (!s) return { rotulo: 'Consultando...', cor: 'default' as const };
  switch (s.situacao) {
    case 'ok': return { rotulo: 'Válido', cor: 'success' as const };
    case 'vencendo': return { rotulo: 'Vencendo', cor: 'warning' as const };
    case 'vencido': return { rotulo: 'Vencido', cor: 'error' as const };
    case 'erro': return { rotulo: 'Com problema', cor: 'error' as const };
    default: return { rotulo: 'Sem certificado', cor: 'default' as const };
  }
};

interface ICertificadoDigitalProps {
  // A tela de Configurações guarda a senha do certificado e reenvia ao salvar
  // o cadastro — precisa saber da senha nova pra não voltar a antiga.
  onSenhaAtualizada: (senha: string) => void;
}

export const CertificadoDigital: React.FC<ICertificadoDigitalProps> = ({ onSenhaAtualizada }) => {
  const theme = useTheme();
  const status = useStatusCertificado();
  const [aberto, setAberto] = useState(false);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [senha, setSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Garante a situação atual ao abrir a tela (não a do cache de horas atrás).
  useEffect(() => { atualizarStatusCertificado(); }, []);

  const abrir = () => {
    setArquivo(null);
    setSenha('');
    setErro('');
    setMostrarSenha(false);
    setAberto(true);
  };

  const enviar = async () => {
    if (!arquivo && !senha) { setErro('Selecione o arquivo do certificado (.pfx) ou informe a nova senha.'); return; }
    if (arquivo && !senha) { setErro('Informe a senha do certificado.'); return; }
    setErro('');
    setEnviando(true);
    try {
      const form = new FormData();
      form.append('senha', senha);
      if (arquivo) form.append('file', arquivo);
      const { data } = await Api.put<IStatusCertificado>('/empresa/certificado', form);
      atualizarStatusCertificado(data);
      onSenhaAtualizada(senha);
      setAberto(false);
      setSucesso(arquivo
        ? `Certificado salvo! Válido até ${dataBR(data.validoAte)}.`
        : 'Senha do certificado atualizada.');
    } catch (e) {
      setErro(axios.isAxiosError(e)
        ? (e.response?.data as { errors?: { default?: string } })?.errors?.default || 'Não foi possível enviar o certificado.'
        : 'Não foi possível enviar o certificado.');
    } finally {
      setEnviando(false);
    }
  };

  const visual = situacaoVisual(status);
  const corBorda = visual.cor === 'default' ? theme.palette.divider : theme.palette[visual.cor].main;

  return (
    <Box sx={{ p: 2, borderRadius: '12px', border: `1px solid ${alpha(corBorda, 0.5)}`, bgcolor: alpha(corBorda, 0.04) }}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
        <VerifiedUserRoundedIcon color={visual.cor === 'default' ? 'action' : visual.cor} />
        <Typography fontWeight={700} flex={1}>Certificado digital (A1)</Typography>
        <Chip size="small" label={visual.rotulo} color={visual.cor} />
      </Box>

      {status && status.situacao !== 'sem_certificado' && status.validoAte ? (
        <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: 'repeat(3, auto)' }} columnGap={4} rowGap={1} mb={1.5}>
          <Box>
            <Typography variant="caption" color="text.secondary">Titular</Typography>
            <Typography fontWeight={600} noWrap>{status.titular || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Válido até</Typography>
            <Typography fontWeight={800} color={status.situacao === 'ok' ? 'success.main' : status.situacao === 'vencendo' ? 'warning.dark' : 'error.main'}>
              {dataBR(status.validoAte)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">{status.situacao === 'vencido' ? 'Situação' : 'Faltam'}</Typography>
            <Typography fontWeight={700}>
              {status.situacao === 'vencido'
                ? 'vencido'
                : typeof status.diasRestantes === 'number'
                  ? (status.diasRestantes <= 0 ? 'vence hoje' : `${status.diasRestantes} dia${status.diasRestantes === 1 ? '' : 's'}`)
                  : '—'}
            </Typography>
          </Box>
        </Box>
      ) : null}

      {status && status.situacao !== 'ok' && (
        <Alert severity={status.situacao === 'vencendo' ? 'warning' : status.situacao === 'sem_certificado' ? 'info' : 'error'} sx={{ mb: 1.5 }}>
          {status.mensagem}
        </Alert>
      )}
      {sucesso && <Alert severity="success" onClose={() => setSucesso('')} sx={{ mb: 1.5 }}>{sucesso}</Alert>}

      <Box display="flex" gap={1} flexWrap="wrap">
        <Button variant="contained" startIcon={<UploadFileRoundedIcon />} onClick={abrir} sx={{ borderRadius: '12px', textTransform: 'none' }}>
          {status?.situacao === 'sem_certificado' ? 'Enviar certificado' : 'Enviar novo certificado'}
        </Button>
      </Box>

      <Dialog open={aberto} onClose={() => !enviando && setAberto(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Certificado digital</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selecione o arquivo <b>.pfx</b> (ou .p12) e a senha. O SIAC confere se a senha está certa, se o certificado está
            válido e se é do CNPJ da empresa <b>antes</b> de salvar — se algo estiver errado, o certificado atual continua valendo.
          </Typography>
          <input
            ref={inputRef}
            type="file"
            accept=".pfx,.p12"
            hidden
            onChange={(e) => { setArquivo(e.target.files?.[0] || null); setErro(''); e.target.value = ''; }}
          />
          <Button
            fullWidth
            variant="outlined"
            startIcon={<UploadFileRoundedIcon />}
            onClick={() => inputRef.current?.click()}
            disabled={enviando}
            sx={{ justifyContent: 'flex-start', textTransform: 'none', borderRadius: '12px', mb: 2, height: 44 }}
          >
            {arquivo ? arquivo.name : 'Escolher arquivo do certificado'}
          </Button>
          <TextField
            fullWidth
            size="small"
            label="Senha do certificado"
            type={mostrarSenha ? 'text' : 'password'}
            value={senha}
            onChange={(e) => { setSenha(e.target.value); setErro(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') enviar(); }}
            disabled={enviando}
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setMostrarSenha((v) => !v)} edge="end">
                    {mostrarSenha ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            helperText={arquivo ? ' ' : 'Sem escolher arquivo, só atualiza a senha do certificado atual.'}
          />
          {erro && <Alert severity="error" sx={{ mt: 1 }}>{erro}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAberto(false)} disabled={enviando}>Cancelar</Button>
          <Button variant="contained" onClick={enviar} disabled={enviando} startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : undefined}>
            {enviando ? 'Conferindo...' : arquivo ? 'Conferir e salvar' : 'Salvar senha'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
